import React, { useState, forwardRef } from "react";
import ReactDOM from "react-dom";
import { FaSearch } from "react-icons/fa";
import { CouponCard } from "../CouponCard/CouponCard";
import WebCodeModals from "../WebCodeModals/WebCodeModals";
import { isCouponInWishlist } from "../../services/wishlistService";
import { trackCouponRedeemAttempt, trackCouponRedeemSuccess, trackCouponRedeemFail } from "../../utils/analytics";
import "./VenueCouponsSection.css";

/**
 * VenueCouponsSection - Handles all coupon display and redemption logic
 * Wraps CouponCard and manages coupon state, modals, and interactions
 */
const VenueCouponsSection = forwardRef(({
  coupons,
  setCoupons,
  wishlistCoupons,
  savingCouponId,
  handleWishlistToggle,
  isPublicMode,
  customerToken,
  apiUrl,
  venueId,
  isInterested,
  venueHasActiveOffers,
  handleInterestToggle,
  interestedLoading,
  triggerLogin,
  showToast,
  updateWishlistCount,
  getCustomerWishlist,
  error = "",
  onViewOtherVenues,
}, ref) => {
  // Confirm modal state
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingCoupon, setPendingCoupon] = useState(null);

  // Second modal for web_code coupons (shows discount code after redemption)
  const [showCodeModal, setShowCodeModal] = useState(false);
  const [redeemedWebCodeCoupon, setRedeemedWebCodeCoupon] = useState(null);

  // Track in-flight redemption requests to prevent double-redeem
  const [redeemingIds, setRedeemingIds] = useState(new Set());

  // Pagination state for coupons
  const [readyCouponsToShow, setReadyCouponsToShow] = useState(3);
  const [redeemedCouponsToShow, setRedeemedCouponsToShow] = useState(5);

  // Check if web_code coupon is currently active based on time window
  const isWebCodeActive = (coupon) => {
    if (coupon.coupon_type !== 'web_code') return true;

    if (!coupon.active_start_time || !coupon.active_end_time) return false;

    try {
      const timezone = coupon.active_timezone || 'America/Chicago';
      const now = new Date();

      const currentTimeStr = now.toLocaleTimeString('en-US', {
        timeZone: timezone,
        hour12: false,
        hour: '2-digit',
        minute: '2-digit'
      });

      const parseTime = (timeStr) => {
        const parts = timeStr.split(':');
        const hours = parseInt(parts[0], 10);
        const minutes = parseInt(parts[1], 10);
        return hours * 60 + minutes;
      };

      const currentMinutes = parseTime(currentTimeStr);
      const startMinutes = parseTime(coupon.active_start_time);
      const endMinutes = parseTime(coupon.active_end_time);

      if (startMinutes <= endMinutes) {
        return currentMinutes >= startMinutes && currentMinutes <= endMinutes;
      } else {
        return currentMinutes >= startMinutes || currentMinutes <= endMinutes;
      }
    } catch (err) {
      console.error('Error checking web_code active time:', err);
      return false;
    }
  };

  const openConfirm = React.useCallback((coupon) => {
    if (isPublicMode) {
      triggerLogin();
      return;
    }
    setPendingCoupon(coupon);
    setConfirmOpen(true);
  }, [isPublicMode, triggerLogin]);

  const closeConfirm = () => {
    setConfirmOpen(false);
    setPendingCoupon(null);
  };

  const closeCodeModal = () => {
    setShowCodeModal(false);
    setRedeemedWebCodeCoupon(null);
  };

  const confirmRedeem = async () => {
    if (!pendingCoupon) return;

    const couponIssueId = pendingCoupon.coupon_issue_id;

    // Prevent double-redeem if a request for this coupon is already in flight
    if (redeemingIds.has(couponIssueId)) return;
    setRedeemingIds((prev) => new Set(prev).add(couponIssueId));

    const markRedeemed = () =>
      setCoupons((prev) =>
        prev.map((c) =>
          c.coupon_issue_id === couponIssueId
            ? { ...c, status: "redeemed", redeemed_at: new Date().toISOString() }
            : c
        )
      );

    const clearInFlight = () =>
      setRedeemingIds((prev) => {
        const next = new Set(prev);
        next.delete(couponIssueId);
        return next;
      });

    const doRedeem = async () => {
      trackCouponRedeemAttempt(couponIssueId, pendingCoupon.venue_id, customerToken, 'coupon_list');

      const res = await fetch(`${apiUrl}/coupon/redeem`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ customer_token: customerToken, coupon_issue_id: couponIssueId }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        // 409 = already redeemed (e.g. concurrent tap) — treat as success in the UI
        if (res.status === 409) {
          markRedeemed();
          showToast("Coupon already redeemed", "info");
          return;
        }
        throw new Error(err.detail || "Redemption failed");
      }

      markRedeemed();
      trackCouponRedeemSuccess(couponIssueId, pendingCoupon.venue_id, customerToken, 'coupon_list');

      try {
        const wishlistData = await getCustomerWishlist(customerToken);
        updateWishlistCount((wishlistData?.saved_coupons || []).length);
      } catch (err) {
        console.error("Failed to update wishlist count:", err);
      }

      return true; // signals success to callers that need it
    };

    const isWebCode = pendingCoupon.coupon_type === 'web_code';

    if (isWebCode) {
      closeConfirm();
      try {
        const ok = await doRedeem();
        if (ok) {
          setRedeemedWebCodeCoupon(pendingCoupon);
          setShowCodeModal(true);
          showToast("Coupon redeemed successfully!", "success");
        }
      } catch (e) {
        trackCouponRedeemFail(couponIssueId, pendingCoupon.venue_id, e.message, 'coupon_list');
        alert(e.message || "Redemption failed. Please try again.");
      } finally {
        clearInFlight();
      }
      return;
    }

    // Standard coupon redemption flow
    closeConfirm();
    const couponElement = document.querySelector(`[data-coupon-id="${couponIssueId}"]`);
    if (couponElement) couponElement.classList.add('tearing');

    setTimeout(async () => {
      try {
        const ok = await doRedeem();
        if (ok) showToast("Coupon redeemed successfully!", "success");
      } catch (e) {
        trackCouponRedeemFail(couponIssueId, pendingCoupon.venue_id, e.message, 'coupon_list');
        alert(e.message || "Redemption failed. Please try again.");
        if (couponElement) couponElement.classList.remove('tearing');
      } finally {
        clearInFlight();
      }
    }, 100);
  };

  // Filter coupons
  const now = new Date();
  const tenDaysAgo = new Date(now.getTime() - 10 * 24 * 60 * 60 * 1000);

  const readyCoupons = isPublicMode
    ? coupons
    : coupons.filter(
      (c) => c.status === "ready" && new Date(c.expires_at) > now
    );

  const redeemedCoupons = isPublicMode
    ? []
    : coupons.filter((c) => {
      if (c.status !== "redeemed") return false;
      if (!c.redeemed_at) return false;
      const redeemedDate = new Date(c.redeemed_at);
      const expiryDate = c.expires_at ? new Date(c.expires_at) : null;
      if (redeemedDate < tenDaysAgo) return false;
      if (expiryDate && expiryDate <= now) return false;
      return true;
    });

  // Render individual coupon card
  const renderCouponCard = (c) => {
    const expiry = c.expires_at
      ? new Date(c.expires_at).toLocaleDateString()
      : null;
    const isExpired =
      c.status === "expired" || new Date(c.expires_at) <= now;
    const isRedeemed = c.status === "redeemed";
    const isWebCode = c.coupon_type === 'web_code';
    const webCodeActive = isWebCode ? isWebCodeActive(c) : true;
    const isReady = c.status === "ready" && !isExpired && webCodeActive;
    const isSaved = isCouponInWishlist(wishlistCoupons, c.coupon_id);
    const isSaving = savingCouponId === c.coupon_id;

    return (
      <li
        key={c.coupon_issue_id || c.coupon_id}
        data-coupon-id={c.coupon_issue_id || c.coupon_id}
      >
        <CouponCard
          openConfirm={openConfirm}
          coupon={c}
          title={c.title}
          description={c.description || ""}
          frequency={expiry ? (c.frequency || "Limited") : ""}
          expiration={
            expiry
              ? isExpired
                ? `Expired ${expiry}`
                : `Use by ${expiry}`
              : "No Expiration"
          }
          redeemed={isRedeemed && c.redeemed_at}
          isSaving={isSaving}
          isSaved={isSaved}
          isReady={isReady}
          handleWishlistToggle={handleWishlistToggle}
          disabled={!isReady && !isPublicMode}
          requiresLogin={isPublicMode}
        />
      </li>
    );
  };

  return (
    <>
      <section ref={ref}>
        <header className="coupons-section-header">
          <h2 className="coupons-section-title">
            {isPublicMode ? "Available Coupons" : "My Coupons"}
            {readyCoupons.length > 0 && (
              <span className="deals-count-badge">{readyCoupons.length}</span>
            )}
          </h2>
        </header>

        {error && <div className="coupon-error">{error}</div>}

        {/* Show coupons or empty state */}
        {readyCoupons.length > 0 || redeemedCoupons.length > 0 ? (
          <>
            {/* Ready Coupons */}
            {readyCoupons.length > 0 && (
              <>
                <div className="coupons-list">
                  {readyCoupons.slice(0, readyCouponsToShow).map((coupon) => renderCouponCard(coupon))}
                </div>
                {readyCoupons.length > 3 && (
                  <div className="show-more-container">
                    {readyCouponsToShow < readyCoupons.length ? (
                      <button
                        className="btn-show-more"
                        onClick={() => setReadyCouponsToShow(readyCoupons.length)}
                      >
                        Show All ({readyCoupons.length} coupons)
                      </button>
                    ) : (
                      <button
                        className="btn-show-less"
                        onClick={() => setReadyCouponsToShow(3)}
                      >
                        Show Less
                      </button>
                    )}
                  </div>
                )}
              </>
            )}

            {/* Redeemed Coupons - Show even if no ready coupons */}
            {redeemedCoupons.length > 0 && (
              <div className="redeemed-coupons-section">
                <h3 className="redeemed-section-title">Redeemed</h3>
                <div className="coupons-list">
                  {redeemedCoupons.slice(0, redeemedCouponsToShow).map((coupon) => renderCouponCard(coupon))}
                </div>
                {redeemedCoupons.length > 5 && (
                  <div className="show-more-container">
                    {redeemedCouponsToShow < redeemedCoupons.length ? (
                      <button
                        className="btn-show-more"
                        onClick={() => setRedeemedCouponsToShow(prev => prev + 5)}
                      >
                        Show More ({redeemedCoupons.length - redeemedCouponsToShow} remaining)
                      </button>
                    ) : (
                      <button
                        className="btn-show-less"
                        onClick={() => setRedeemedCouponsToShow(5)}
                      >
                        Show Less
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </>
        ) : (
          <div className="empty-coupons-state">
            {/* Show "no active coupons" message only when venue has offers but none active */}
            {venueHasActiveOffers === true && (
              <>
                <p className="no-coupons-message">
                  {isPublicMode
                    ? "No coupons available at this venue right now."
                    : "No active coupons. Check back next week!"}
                </p>

                {onViewOtherVenues && (
                  <button
                    className="redeem-button"
                    onClick={onViewOtherVenues}
                  >
                    <FaSearch /> View Other Venues
                  </button>
                )}
              </>
            )}

            {/* Show interest section when venue has no offers at all */}
            {venueHasActiveOffers === false && (
              <div className="interest-section">
                <p className="interest-message">
                  Want exclusive discounts here?
                  {isPublicMode
                    ? " Sign in to show your interest."
                    : " Let us know and we'll prioritize this venue."}
                </p>
                <button
                  className={`btn-interest ${isInterested ? 'interested' : ''}`}
                  onClick={handleInterestToggle}
                  disabled={interestedLoading}
                >
                  {interestedLoading
                    ? "Loading..."
                    : isPublicMode
                      ? "Sign In to Show Interest"
                      : isInterested
                        ? "✓ Interested"
                        : "I'm Interested"}
                </button>
              </div>
            )}
          </div>
        )}
      </section>

      {/* Redeem Confirmation Modal - Portal ensures proper z-index above gallery */}
      {confirmOpen && pendingCoupon && ReactDOM.createPortal(
        <div className="modal-backdrop redeem-confirmation-modal" onClick={closeConfirm}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <h3 className="modal-title">
              Redeem Coupon
            </h3>

            {pendingCoupon.coupon_type === 'web_code' ? (
              <>
                <div className="modal-warning-box">
                  <p className="modal-warning-text">
                    A discount code will be displayed for use on the third-party ordering website.
                  </p>
                </div>

                <div className="modal-character-container">
                  <div className="modal-character">💻</div>
                </div>

                <p className="modal-confirm-text">
                  Ready to redeem this coupon?
                </p>
              </>
            ) : (
              <>
                <div className="modal-warning-box">
                  <p className="modal-warning-text">
                    Show staff while redeeming.
                    <br />
                    Only counts if they see the <strong className="text-emphasis-red">FULL</strong> process.
                  </p>
                </div>

                <div className="modal-character-container">
                  <div className="modal-character">🤵</div>
                </div>

                <p className="modal-confirm-text">
                  Ready to redeem this coupon?
                </p>
              </>
            )}

            <div className="modal-button-group">
              <button
                onClick={closeConfirm}
                className="modal-btn-cancel"
              >
                Cancel
              </button>
              <button
                onClick={confirmRedeem}
                className="modal-btn-confirm"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Web Code Modal - Shows discount code for delivery coupons */}
      <WebCodeModals
        coupon={redeemedWebCodeCoupon}
        isOpen={showCodeModal}
        onClose={closeCodeModal}
        showToast={showToast}
      />
    </>
  );
});

VenueCouponsSection.displayName = 'VenueCouponsSection';

export default VenueCouponsSection;
